import { SENALES_POR_LOTE_MAX, type Senal } from '@ahora-patagonia/contracts';

/**
 * El emisor de señales del navegador.
 *
 * **Junta y manda de a lotes.** Una lectura de nota genera tres señales (vista, terminada, tiempo)
 * y mandarlas sueltas serían tres pedidos por nota. Se acumulan en memoria y salen cuando pasa
 * cualquiera de estas cosas: el lote se llena, pasa la ventana de espera, o la página se está
 * yendo — que es el caso importante, porque el tiempo de lectura se sabe recién ahí.
 *
 * **`sendBeacon` primero.** Es la única forma de que un pedido sobreviva a que el lector navegue o
 * cierre la pestaña en el mismo instante; un `fetch` común se cancela. `keepalive` es el respaldo
 * para el navegador que no lo tenga.
 *
 * **Nada de esto puede romper la pantalla.** Todo va envuelto: si el almacenamiento está
 * bloqueado, si `sendBeacon` devuelve `false`, si el JSON no serializa — la señal se pierde y el
 * lector no se entera. Es la misma decisión que toma el servidor del otro lado.
 */

/** Cuánto se espera antes de mandar un lote que no se llenó. */
const VENTANA_MS = 3_000;

/** El path es de primera parte y no dice "analytics", "track" ni "collect". Ver el controller. */
const URL_SENALES = '/senales';

let pendientes: Senal[] = [];
let temporizador: number | undefined;
let escuchando = false;

/**
 * Encola una señal. No manda nada por sí sola salvo que el lote se llene.
 *
 * En el servidor (SSR) no hace nada: no hay navegador que cuente, y el HTML que se está armando
 * puede terminar sirviéndose a mil lectores desde el caché.
 */
export function emitir(senal: Senal): void {
  if (typeof window === 'undefined') return;
  // El prerender de Chrome renderiza la página entera en una pestaña que nadie mira. Es el único
  // bot que ejecuta JavaScript y que el servidor no puede reconocer, porque *es* el navegador del
  // lector. Sus señales no se encolan; cuando la pestaña se activa de verdad, el efecto vuelve a
  // correr y ahí sí cuentan.
  const doc = document as Document & { prerendering?: boolean };
  if (doc.prerendering === true) return;

  pendientes.push(senal);
  asegurarEscuchas();

  if (pendientes.length >= SENALES_POR_LOTE_MAX) {
    enviar();
    return;
  }
  if (temporizador === undefined) {
    temporizador = window.setTimeout(enviar, VENTANA_MS);
  }
}

/** Manda lo que haya pendiente, ya. Lo llama el `pagehide` y también se puede forzar. */
export function enviar(): void {
  if (temporizador !== undefined) {
    window.clearTimeout(temporizador);
    temporizador = undefined;
  }
  if (pendientes.length === 0) return;

  // Se vacía **antes** de mandar: si el envío falla no se reintenta, y dejar la cola llena haría
  // que el próximo lote arrastre señales viejas con tiempos que ya no significan nada.
  const lote = pendientes.slice(0, SENALES_POR_LOTE_MAX);
  pendientes = pendientes.slice(SENALES_POR_LOTE_MAX);

  try {
    const cuerpo = JSON.stringify({ senales: lote });
    // `type: 'application/json'` y mismo origen: sin preflight, porque no es cross-origin.
    const blob = new Blob([cuerpo], { type: 'application/json' });
    if (typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(URL_SENALES, blob)) {
      if (pendientes.length > 0) enviar();
      return;
    }
    void fetch(URL_SENALES, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: cuerpo,
      keepalive: true,
    }).catch(() => undefined);
    if (pendientes.length > 0) enviar();
  } catch {
    /* que no se pueda medir no puede romper la pantalla */
  }
}

/**
 * `pagehide` y no `beforeunload` ni `unload`: es el único que dispara de forma confiable en móviles
 * y el único compatible con el caché de retroceso del navegador. Se registra una sola vez.
 */
function asegurarEscuchas(): void {
  if (escuchando) return;
  escuchando = true;
  window.addEventListener('pagehide', enviar);
  // La pestaña que se oculta puede no volver nunca (el sistema mata la app en segundo plano). Es
  // el último momento garantizado para mandar en un teléfono.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') enviar();
  });
}

/** Vacía la cola sin mandar. Solo para los tests, que no comparten estado entre casos. */
export function limpiarParaTests(): void {
  pendientes = [];
  if (typeof window !== 'undefined' && temporizador !== undefined) {
    window.clearTimeout(temporizador);
  }
  temporizador = undefined;
}
